import React, { useEffect, useState } from 'react';
import { ActivityCard } from './ui';
import type { Activity } from './ui';
import './ActivityHistory.css';

interface FitFileEntity {
  partitionKey: 'processed' | 'unprocessed';
  rowKey: string;
  fileName: string;
  dropboxPath: string;
  dropboxFileId: string;
  fileSize: number;
  modifiedDate: string;
  syncedAt: string;
  garminSynced?: boolean;
  garminActivityId?: string;
  garminSyncedAt?: string;
  garminSyncError?: string;
}

interface ActivityHistoryResponse {
  activities: FitFileEntity[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

type FilterType = 'all' | 'uploaded' | 'failed' | 'pending';

interface ActivityHistoryProps {
  refreshTrigger?: number;
}

export const ActivityHistory: React.FC<ActivityHistoryProps> = ({ refreshTrigger }) => {
  const [data, setData] = useState<ActivityHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState<FilterType>('all');

  const fetchActivities = async (page: number, filterType: FilterType) => {
    try {
      setIsLoading(true);
      setError(null);

      const headers: Record<string, string> = {};
      const token = localStorage.getItem('authToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...(filterType !== 'all' && { filter: filterType }),
      });

      const response = await fetch(`/api/activities?${params}`, { headers });

      if (!response.ok) {
        throw new Error('Failed to fetch activities');
      }

      const responseData = await response.json();
      setData(responseData);
    } catch (err) {
      console.error('Error fetching activities:', err);
      setError('Failed to load activity history');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities(currentPage, filter);
  }, [currentPage, filter, refreshTrigger]);

  const convertToActivity = (file: FitFileEntity): Activity => {
    if (file.garminSyncError) {
      return {
        fileName: file.fileName,
        action: 'error',
        reason: file.garminSyncError,
        timestamp: file.garminSyncedAt || file.syncedAt,
      };
    }

    if (file.garminSynced) {
      return {
        fileName: file.fileName,
        action: 'uploaded',
        reason: 'Successfully uploaded to Garmin Connect',
        activityId: file.garminActivityId,
        timestamp: file.garminSyncedAt || file.syncedAt,
      };
    }

    return {
      fileName: file.fileName,
      action: 'skipped',
      reason: 'Pending upload',
      timestamp: file.syncedAt,
    };
  };

  const handleFilterChange = (newFilter: FilterType) => {
    setFilter(newFilter);
    setCurrentPage(1); // Reset to first page on filter change
  };

  if (error) {
    return (
      <div className="activity-history">
        <div className="activity-history__error">
          <p>{error}</p>
          <button onClick={() => fetchActivities(currentPage, filter)} className="activity-history__retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="activity-history">
      <div className="activity-history__header">
        <div>
          <h2 className="activity-history__title">Activity History</h2>
          <p className="activity-history__subtitle">Track all your synced fitness files</p>
        </div>
      </div>

      <div className="activity-history__filters">
        <button
          className={`activity-history__filter-button ${filter === 'all' ? 'activity-history__filter-button--active' : ''}`}
          onClick={() => handleFilterChange('all')}
        >
          All
          {data && <span className="activity-history__filter-badge">{data.total}</span>}
        </button>
        <button
          className={`activity-history__filter-button ${filter === 'uploaded' ? 'activity-history__filter-button--active' : ''}`}
          onClick={() => handleFilterChange('uploaded')}
        >
          ✅ Uploaded
        </button>
        <button
          className={`activity-history__filter-button ${filter === 'pending' ? 'activity-history__filter-button--active' : ''}`}
          onClick={() => handleFilterChange('pending')}
        >
          ⏳ Pending
        </button>
        <button
          className={`activity-history__filter-button ${filter === 'failed' ? 'activity-history__filter-button--active' : ''}`}
          onClick={() => handleFilterChange('failed')}
        >
          ❌ Failed
        </button>
      </div>

      {isLoading ? (
        <div className="activity-history__loading">
          <div className="activity-history__spinner"></div>
          <p>Loading activities...</p>
        </div>
      ) : data && data.activities.length > 0 ? (
        <>
          <div className="activity-history__list">
            {data.activities.map((file) => (
              <ActivityCard key={file.rowKey} activity={convertToActivity(file)} showDetails={false} />
            ))}
          </div>

          {data.totalPages > 1 && (
            <div className="activity-history__pagination">
              <button
                className="activity-history__pagination-button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>

              <div className="activity-history__pagination-info">
                Page {currentPage} of {data.totalPages}
              </div>

              <button
                className="activity-history__pagination-button"
                onClick={() => setCurrentPage((p) => Math.min(data.totalPages, p + 1))}
                disabled={currentPage === data.totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="activity-history__empty">
          <span className="activity-history__empty-icon">📭</span>
          <p className="activity-history__empty-text">No activities found</p>
          <p className="activity-history__empty-subtext">
            {filter === 'all' ? 'Start syncing to see your activity history here' : `No ${filter} activities`}
          </p>
        </div>
      )}
    </div>
  );
};
